import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/services/api';
import { useToast } from '@/shared/toast';
import { useConfirm } from '@/shared/confirm';
import { saveFile, openFile } from '@/shared/fileSave';
import { isInNativeWrapper, requestNativeDocumentUpload } from '@/services/nativeBridge';

// Allineato al limite API (DocumentEndpoints.MaxSizeBytes) + nginx/Kestrel (60MB).
export const MAX_UPLOAD_MB = 50;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;
// Estensioni accettate (coerenti con AllowedContentTypes lato API), Word incluso.
export const UPLOAD_ACCEPT = '.pdf,.txt,.jpg,.png,.gif,.bmp,.webp,.xls,.xlsx,.doc,.docx,.csv';

/** Documento (livello o Rifornimenti): shape di DocumentDto lato API. */
export interface DocumentItem {
  id: string;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
}

/** Risultato ricerca: DocumentItem + percorso testuale della cartella (DocumentSearchResultDto). */
export interface DocumentSearchResult extends DocumentItem {
  /** "cartella1/sottocartella2" oppure "" se il file e' in root. */
  path: string;
}

/**
 * Nome leggibile: alcuni file storici/migrati (e l'upload nativo Android) hanno
 * il nome percent-encoded (gli spazi appaiono come %20). Decodifico in modo
 * difensivo per la visualizzazione; se il nome contiene un '%' non valido come
 * escape, decodeURIComponent lancia -> tengo il nome grezzo.
 */
export function prettyName(name: string): string {
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

interface UseDocumentActionsOptions {
  category: string;
  /** Cartella di destinazione dell'upload; null = root (Rifornimenti resta sempre null). */
  folderId: string | null;
}

interface UploadOptions {
  /** Rinomina il file mantenendo l'estensione (Rifornimenti: nome scontrino). */
  rename?: string;
  /** Tetto di file caricabili per batch (Rifornimenti: 1). Assente = tutti. */
  limit?: number;
}

interface UseDocumentActions {
  uploading: boolean;
  uploadFiles: (files: File[], opts?: UploadOptions) => Promise<void>;
  openDocument: (doc: DocumentItem, force?: boolean) => Promise<void>;
  deleteDocument: (doc: DocumentItem) => Promise<void>;
  /** Delega l'upload al wrapper nativo (Android WebView non carica file). true se inoltrato. */
  requestNativeUpload: () => boolean;
}

/**
 * Logica documenti condivisa (upload / apri / scarica / elimina), parametrizzata
 * per categoria + cartella. Riusata da DocumentsPage (Rifornimenti, folderId=null)
 * e da FolderBrowser (ODG/POS/Manuali, folderId del livello corrente): un'unica
 * fonte di verita' per FormData/openFile/delete, senza duplicare la logica.
 *
 * Le invalidazioni usano i prefissi ['documents'] e ['doc-folders'] (match
 * parziale di React Query): un upload/delete aggiorna sia le liste di file di
 * ogni livello sia il flag hasContent delle cartelle.
 */
export function useDocumentActions({ category, folderId }: UseDocumentActionsOptions): UseDocumentActions {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);

  // Carica UN singolo file. Ritorna true/false; NON gestisce spinner/refresh
  // (li fa uploadFiles a livello di batch).
  async function uploadOne(file: File, rename?: string): Promise<boolean> {
    // Validazione size lato client: evita un upload inutile e da' un messaggio
    // chiaro invece del rifiuto opaco del server.
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.show(`"${file.name}": troppo grande (max ${MAX_UPLOAD_MB} MB)`, 'error');
      return false;
    }
    try {
      const fd = new FormData();
      // rename (Rifornimenti): rinomino il file (3° arg di append = filename
      // multipart) mantenendo l'estensione originale. Le altre categorie
      // mantengono il nome originale del file.
      const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '';
      const finalName = rename?.trim() ? `${rename.trim()}${ext}` : file.name;
      fd.append('file', file, finalName);
      fd.append('category', category);
      // Cartella di destinazione: stringa vuota per la root (il server ignora
      // il campo per Rifornimenti, che resta un buffer piatto).
      fd.append('folderId', folderId ?? '');
      await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return true;
    } catch (err) {
      // Mostra il MOTIVO reale (tipo non consentito / size / firma) per-file.
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      console.error('Upload documento fallito:', err);
      toast.show(`"${file.name}": ${msg ?? 'errore upload'}`, 'error');
      return false;
    }
  }

  // Carica uno o piu' file IN SEQUENZA. A fine batch: un solo refresh + riepilogo.
  async function uploadFiles(files: File[], opts?: UploadOptions): Promise<void> {
    if (!files.length || uploading) return;
    const list = opts?.limit ? files.slice(0, opts.limit) : files;
    setUploading(true);
    toast.show(list.length === 1
      ? `Caricamento "${list[0].name}" (${Math.round(list[0].size / 1024)} KB)…`
      : `Caricamento di ${list.length} file…`);
    let ok = 0;
    for (const f of list) {
      if (await uploadOne(f, opts?.rename)) ok++;
    }
    qc.invalidateQueries({ queryKey: ['documents'] });
    qc.invalidateQueries({ queryKey: ['doc-folders'] });
    const fail = list.length - ok;
    if (list.length === 1) {
      toast.show(ok ? 'Documento caricato' : 'Errore upload', ok ? 'success' : 'error');
    } else {
      toast.show(`Caricati ${ok}/${list.length}${fail ? ` — ${fail} falliti` : ''}`, fail ? 'error' : 'success');
    }
    setUploading(false);
  }

  // Bryan: il <a href="/api/documents/{id}/download"> dava 401 perche' l'anchor
  // HTML nativo non passa l'header Authorization. Fetch via axios (che ha gia'
  // l'interceptor JWT) + Blob + open/saveFile.
  async function openDocument(doc: DocumentItem, force = false): Promise<void> {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const blob = res.data as Blob;
      const mime = blob.type || 'application/octet-stream';
      const name = prettyName(doc.fileName);
      if (force) {
        // Scarica: saveFile gestisce browser (download) e app nativa (bridge file:save).
        await saveFile(blob, name, mime);
      } else {
        // Visualizza: openFile apre inline nel browser (nuova scheda) e, nell'app
        // nativa, passa dal bridge file:open -> visualizzatore di sistema.
        await openFile(blob, name, mime);
      }
    } catch {
      toast.show('Errore apertura documento', 'error');
    }
  }

  async function deleteDocument(doc: DocumentItem): Promise<void> {
    const ok = await confirm({
      title: 'Elimina documento',
      message: `Eliminare "${prettyName(doc.fileName)}"?`,
      confirmLabel: 'Elimina', variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['doc-folders'] });
      toast.show('Documento eliminato');
    } catch {
      toast.show('Errore eliminazione', 'error');
    }
  }

  // App nativa: l'upload lo fa il wrapper (picker + fetch nativi), inoltrando
  // anche il folderId di destinazione (buffer piatto per Rifornimenti -> null).
  function requestNativeUpload(): boolean {
    return requestNativeDocumentUpload(category, folderId ?? undefined);
  }

  return { uploading, uploadFiles, openDocument, deleteDocument, requestNativeUpload };
}

/** True se siamo nel wrapper nativo (re-export di comodo per i consumer del hook). */
export { isInNativeWrapper };
